import type { ProjectMenuItemResponse } from '@protoss/shared';
import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';

interface UseProjectMenuResult {
  items: ProjectMenuItemResponse[];
  isLoading: boolean;
  error: string | null;
}

/**
 * useProjectMenu - fetch the dynamic project navigation menu for a project.
 *
 * Calls GET /api/project/:uuid/menu, which returns menu items with full
 * entitlement and configuration state resolved server-side.
 *
 * The hook re-fetches automatically when projectUuid changes.
 *
 * Authorization: Firebase ID token is attached to the request.
 * Role-based and entitlement-based filtering is handled by the API;
 * the frontend only renders what the API returns.
 */
export function useProjectMenu(projectUuid: string | undefined): UseProjectMenuResult {
  const [items, setItems] = React.useState<ProjectMenuItemResponse[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchMenu = React.useCallback(async () => {
    if (!projectUuid) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiFetch<{ items: ProjectMenuItemResponse[] }>(
        `/api/project/${projectUuid}/menu`
      );
      setItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project menu');
    } finally {
      setIsLoading(false);
    }
  }, [projectUuid]);

  React.useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  return { items, isLoading, error };
}
